import { Icon } from '@hologram-dimension/icon';
import { Popover } from '@hologram-dimension/popover';
import useSimBilling from 'api/sims/useSimBilling';
import Field, { type FieldProps } from 'components/Field/Field';
import { type FC, useMemo } from 'react';
import formatDate from 'utils/dates/formatDate';
import formatDateTime from 'utils/dates/formatDateTime';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import styles from './BillingPeriodField.module.css';

interface BillingPeriodFieldProps {
  hologramSimId?: HologramSimId;
  direction?: FieldProps['direction'];
  label?: string;
}
const BillingPeriodField: FC<BillingPeriodFieldProps> = ({
  hologramSimId,
  direction = 'column',
  label = 'Billing period',
}) => {
  const {
    simBilling,
    error: simBillingError,
    isLoading: simBillingIsLoading,
  } = useSimBilling(hologramSimId);
  const { billingPeriodStart, billingPeriodEnd, simRenewalDate, organizationBillingMethod } =
    simBilling;

  const { sendAnalyticsEvent } = useAnalytics();

  const isPrepaid = organizationBillingMethod === 'prepaid';
  const isPostpaid = organizationBillingMethod === 'postpaid';

  const fieldValue = useMemo(() => {
    if (simBillingError) {
      return '—';
    }
    if (simBillingIsLoading) {
      return 'Loading...';
    }
    return (
      <div className={styles.popoverWithTooltip}>
        {isPostpaid && formatDate(billingPeriodStart, 'month')}
        {isPrepaid &&
          `${formatDate(billingPeriodStart, 'long')} – ${formatDate(billingPeriodEnd, 'long')}`}
        <Popover
          className={styles.popover}
          id="popover-sms-limit"
          content={
            isPrepaid ? (
              <>
                SIM platform fees, prepaid data, and add-on features are charged every 30 days,
                beginning on the day this SIM was activated.{' '}
                {simRenewalDate && (
                  <>
                    Renews{' '}
                    <span className={styles.popoverDate}>
                      {formatDateTime(simRenewalDate, 'label')}.
                    </span>
                  </>
                )}
              </>
            ) : (
              <>
                Data usage is charged monthly, resetting on the 1st of the month at midnight UTC.
                SIM platform fees and add-on features are charged every 30 days, beginning on the
                day this SIM was activated.{' '}
                {simRenewalDate && (
                  <>
                    Next SIM renewal{' '}
                    <span className={styles.popoverDate}>
                      {formatDateTime(simRenewalDate, 'label')}.
                    </span>
                  </>
                )}
              </>
            )
          }
          onOpen={() => {
            if (sendAnalyticsEvent) sendAnalyticsEvent('Info - Billing period');
          }}
        >
          <Icon name="CircleInfo" fill="DdsColorInteractivePrimaryDefault" />
        </Popover>
      </div>
    );
  }, [
    billingPeriodEnd,
    billingPeriodStart,
    isPostpaid,
    isPrepaid,
    sendAnalyticsEvent,
    simBillingError,
    simBillingIsLoading,
    simRenewalDate,
  ]);

  return <Field label={label} value={fieldValue} direction={direction} />;
};
export default BillingPeriodField;
